"use client"

import React, { useEffect, useRef, useState } from "react"

import "./index.scss"

import { createFromIconfontCN } from "@ant-design/icons"

interface IconFontProps {
  type: string
  className?: string
  style?: React.CSSProperties
  onClick?: () => void
  /** iconfont图标将严格按照此宽度来占据布局空间，如不传，则按照fontSize来计算布局空间 */
  width?: number | string
  /** iconfont图标将严格按照此高度来占据布局空间，如不传，则按照fontSize来计算布局空间 */
  height?: number | string
  /** 降级显示内容：当脚本未加载时显示的备用内容 */
  fallback?: React.ReactNode
}

// iconfont 脚本 URL
const ICONFONT_SCRIPT_URL = "https://imgqn.smm.cn/static/metal-monorope/iconfont/iconfont_v2.js"

// 创建 IconFont 组件实例
// createFromIconfontCN 内部会处理脚本加载，避免重复加载
const IconFont1 = createFromIconfontCN({
  scriptUrl: ICONFONT_SCRIPT_URL,
})

/**
 * 检查 iconfont 脚本是否已加载
 * iconfont 脚本加载后会在 body 中插入一个隐藏的 SVG，包含所有 symbol
 */
const isScriptLoaded = (): boolean => {
  if (typeof window === "undefined" || typeof document === "undefined") return false
  const svgSymbols = document.querySelector("svg[style*='position: absolute'][style*='width: 0']")
  return Boolean(svgSymbols?.querySelector("symbol"))
}

// 全局缓存：脚本加载状态
let globalScriptLoaded = false
const loadListeners: any[] = []

/**
 * 通知所有监听器脚本已加载
 */
const notifyScriptLoaded = () => {
  globalScriptLoaded = true
  loadListeners.forEach((listener) => listener())
  loadListeners.length = 0
}

/**
 * 自定义 Hook：监听 iconfont 脚本加载状态
 * 优化版本：
 * 1. 使用全局缓存，避免重复检测
 * 2. 优化轮询策略，使用 requestIdleCallback
 * 3. 减少超时时间，提升用户体验
 * 4. 支持脚本加载失败处理
 */
const useIconfontScript = (): boolean => {
  const [isLoaded, setIsLoaded] = useState(false)
  const checkCountRef = useRef(0)
  const maxCheckCount = 20 // 最多检查20次（2秒）

  useEffect(() => {
    // 如果全局已标记加载完成，立即返回
    if (globalScriptLoaded || isScriptLoaded()) {
      setIsLoaded(true)
      if (!globalScriptLoaded) notifyScriptLoaded()
      return
    }

    const cleanupFns: any[] = []

    // 注册监听器，当脚本加载完成时通知
    const listener = () => setIsLoaded(true)
    loadListeners.push(listener)
    cleanupFns.push(() => {
      const index = loadListeners.indexOf(listener)
      if (index > -1) loadListeners.splice(index, 1)
    })

    // 方案1: MutationObserver 监听 DOM 变化
    const observer = new MutationObserver(() => {
      if (isScriptLoaded()) {
        setIsLoaded(true)
        notifyScriptLoaded()
        observer.disconnect()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    cleanupFns.push(() => observer.disconnect())

    // 方案2: 优化的轮询检查（使用 requestIdleCallback 或 setTimeout）
    const scheduleCheck = () => {
      checkCountRef.current += 1

      if (isScriptLoaded()) {
        setIsLoaded(true)
        notifyScriptLoaded()
        observer.disconnect()
        return
      }

      if (checkCountRef.current >= maxCheckCount) {
        observer.disconnect()
        return
      }

      // 优先使用 requestIdleCallback，降低性能影响
      if (typeof requestIdleCallback !== "undefined") {
        const idleId = requestIdleCallback(() => scheduleCheck(), { timeout: 100 })
        cleanupFns.push(() => cancelIdleCallback(idleId))
      } else {
        const timeoutId = setTimeout(scheduleCheck, 100)
        cleanupFns.push(() => clearTimeout(timeoutId))
      }
    }

    scheduleCheck()

    // 方案3: window load 事件
    const handleLoad = () => {
      // 减少延迟时间，提升响应速度
      setTimeout(() => {
        if (isScriptLoaded()) {
          setIsLoaded(true)
          notifyScriptLoaded()
          observer.disconnect()
        }
      }, 50)
    }

    if (document.readyState === "complete") {
      handleLoad()
    } else {
      window.addEventListener("load", handleLoad)
      cleanupFns.push(() => window.removeEventListener("load", handleLoad))
    }

    // 方案4: 缩短超时时间（兜底方案）
    const finalCheckTimeout = setTimeout(() => {
      if (isScriptLoaded()) {
        setIsLoaded(true)
        notifyScriptLoaded()
      }
      observer.disconnect()
    }, 2000) // 缩短到 2 秒

    cleanupFns.push(() => clearTimeout(finalCheckTimeout))

    return () => {
      cleanupFns.forEach((fn) => fn())
    }
  }, [])

  return isLoaded
}

const IconFont = ({ type, className, width, height, style, fallback, ...resetProps }: IconFontProps) => {
  const isLoaded = useIconfontScript()
  const scriptLoaded = isScriptLoaded()

  // @deprecated width 和 height 已废弃，请使用 style.fontSize 替代
  const iconStyle = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
    ...style,
  }

  const wrapperStyle: React.CSSProperties = {
    ...iconStyle,
    width: iconStyle.width || iconStyle.fontSize || "auto",
    height: iconStyle.height || iconStyle.fontSize || "auto",
    // 优化：移除 visibility hidden，只使用 opacity 实现更流畅的过渡
    opacity: isLoaded ? 1 : 0,
    transition: "opacity 0.2s ease-in-out",
  }

  // 脚本未加载且提供了降级内容：显示降级内容
  if (isLoaded && !scriptLoaded && fallback) {
    return (
      <div className={`icon-wrapper icon-fallback ${className || ""}`} style={wrapperStyle}>
        {fallback}
      </div>
    )
  }

  // 脚本未加载且未提供降级内容：显示占位符（保持布局空间）
  if (isLoaded && !scriptLoaded) {
    return (
      <div className={`icon-wrapper icon-placeholder ${className || ""}`} style={wrapperStyle}>
        <span style={{ opacity: 0 }}>□</span>
      </div>
    )
  }

  // 正在检测或脚本已加载：正常渲染
  return <IconFont1 type={type} {...resetProps} className={`icon-wrapper ${className || ""}`} style={wrapperStyle} />
}

export default IconFont
export type { IconFontProps }
